import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Interface pour les détails de paiement par carte shop
export interface CampayCardShopPaymentDetails {
  amount: number;
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  return_url: string;
  failure_url?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface CampayCardShopPaymentResponse {
  order: {
    id: string;
    amount: number;
    status: string;
    payment_method: string;
    payment_reference: string | null;
    payment_url: string | null;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface CampayCardShopStatusResponse {
  status: 'success' | 'pending' | 'failed';
  data: any;
}

/**
 * Initie un paiement par carte pour une commande shop via la fonction Edge
 */
export const initiateCampayCardShopEdgePayment = async (
  details: CampayCardShopPaymentDetails
): Promise<CampayCardShopPaymentResponse> => {
  try {
    const response = await fetch(`${config.supabaseUrl}/functions/v1/shop-campay-card-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Échec de l'initiation du paiement par carte");
    }

    return await response.json();
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement Campay Card Shop:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement par carte shop via la base de données
 */
export const checkCampayCardShopEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<CampayCardShopStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  console.log(`[CampayCardShopEdge][${requestId}] Début de checkCampayCardShopEdgePaymentStatus - reference: ${reference}, order_id: ${order_id}`);
  
  try {
    // Vérifier le statut de la commande dans la base de données
    console.log(`[CampayCardShopEdge][${requestId}] Vérification du statut dans la base de données pour la commande ${order_id}`);
    
    const { data: order, error } = await supabase
      .from('shop_orders')
      .select('status, payment_reference')
      .eq('id', order_id)
      .single();
    
    if (error) {
      console.error(`[CampayCardShopEdge][${requestId}] Erreur lors de la récupération de la commande:`, error);
      throw new Error(`Erreur lors de la récupération de la commande: ${error.message}`);
    }
    
    if (!order) {
      console.error(`[CampayCardShopEdge][${requestId}] Commande ${order_id} introuvable`);
      throw new Error('Commande introuvable');
    }
    
    console.log(`[CampayCardShopEdge][${requestId}] Statut de la commande ${order_id}: ${order.status}`);
    
    // Vérifier si la commande est marquée comme payée
    if (order.status === 'paid') {
      console.log(`[CampayCardShopEdge][${requestId}] La commande ${order_id} est déjà marquée comme payée`);
      return { 
        status: 'success', 
        data: { 
          order_id,
          payment_reference: reference,
          status: 'paid'
        } 
      };
    }
    
    console.log(`[CampayCardShopEdge][${requestId}] La commande ${order_id} n'est pas encore payée (statut: ${order.status})`);
    
    // Si la commande n'est pas encore payée, vérifier via la fonction edge
    const response = await fetch(`${config.supabaseUrl}/functions/v1/shop-campay-card-initiate/status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({
        reference,
        order_id
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(`[CampayCardShopEdge][${requestId}] Échec de la vérification du statut:`, errorData);
      throw new Error(errorData.error || "Échec de la vérification du statut du paiement");
    }

    const data = await response.json();
    console.log(`[CampayCardShopEdge][${requestId}] Données reçues:`, JSON.stringify(data));
    
    return data;
  } catch (error) {
    console.error(`[CampayCardShopEdge][${requestId}] Erreur lors de la vérification du statut:`, error);
    throw error;
  }
};
